import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Bars2Icon } from "@heroicons/react/20/solid";
import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

function Row({ id, disabled, children }: { id: string; disabled?: boolean; children: ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn("flex items-center gap-2 bg-canvas", isDragging && "relative z-10 rounded-md shadow-pop")}
    >
      {!disabled && (
        <button ref={setActivatorNodeRef} type="button" {...attributes} {...listeners} className="cursor-grab rounded p-1 text-faint hover:bg-hover active:cursor-grabbing" aria-label="Drag to reorder">
          <Bars2Icon className="size-4" />
        </button>
      )}
      <div className="flex min-w-0 flex-1 items-center gap-2">{children}</div>
    </div>
  );
}

/** Vertical list with drag handles; calls onReorder with the new id order. */
export function ReorderList<T extends { id: string }>({
  items,
  onReorder,
  disabled,
  render,
  className,
}: {
  items: T[];
  onReorder: (ids: string[]) => void;
  disabled?: boolean;
  render: (item: T) => ReactNode;
  className?: string;
}) {
  const [order, setOrder] = useState(items.map((i) => i.id));
  useEffect(() => setOrder(items.map((i) => i.id)), [items]);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const byId = new Map(items.map((i) => [i.id, i]));

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(order, order.indexOf(String(active.id)), order.indexOf(String(over.id)));
    setOrder(next);
    onReorder(next);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        <div className={cn("space-y-1", className)}>
          {order.map((id) => {
            const item = byId.get(id);
            return item ? (
              <Row key={id} id={id} disabled={disabled}>
                {render(item)}
              </Row>
            ) : null;
          })}
        </div>
      </SortableContext>
    </DndContext>
  );
}
